import { useState } from 'react';
import { ProfileSection } from '@/components/organisms/ProfileSection';
import { SegmentedControl } from '@/components/organisms/SegmentedControl';
import { QuarterlyPerformanceTable, type QuarterlyRow } from '@/components/organisms/QuarterlyPerformanceTable';
import { PerformanceOverTimeChart } from '@/components/organisms/PerformanceOverTimeChart';
import { NetGrossChurnChart } from '@/components/organisms/NetGrossChurnChart';
import { prettyQuarter } from '@/lib/format';
import { usePlatformProfileContext } from './PlatformProfileLayout';
import { PROFILE_REGIONS, useRegionHistories } from './usePlatformProfile';
import styles from './ProfileTabs.module.css';

const SPLIT_OPTS = [
  { value: 'total', label: 'Total' },
  { value: 'regions', label: 'By region' },
];

/** Summary — Platform Performance → Performance Over Time → Net, Gross, Churn. */
export function SummaryTab() {
  const { id, region, profile } = usePlatformProfileContext();
  const { history, ranks } = profile;
  const { byRegion } = useRegionHistories(id);
  const [split, setSplit] = useState<'total' | 'regions'>('total');

  const regionScope = region ?? 'All regions';
  const span =
    history.length > 0 ? `${prettyQuarter(history[0].quarter)} – ${prettyQuarter(history[history.length - 1].quarter)}` : '';
  const scope = [regionScope, span].filter(Boolean).join(' · ');

  // Table rows, newest first. Split → one row per quarter × region.
  const splitting = split === 'regions' && !region;
  const tableRows: QuarterlyRow[] = splitting
    ? PROFILE_REGIONS.flatMap((r) => (byRegion[r] ?? []).map((h) => ({ ...h, region: r })))
    : history.map((h) => ({ ...h, region: regionScope }));
  tableRows.sort((a, b) => b.quarter.localeCompare(a.quarter) || a.region.localeCompare(b.region));

  const regions = region ? [region] : [...PROFILE_REGIONS];
  const scopeLabel = region ?? `all regions (${PROFILE_REGIONS.join(', ')})`;

  if (!history.length) {
    return (
      <div className={styles.tab}>
        <div className={styles.placeholder}>
          <span className={styles.placeholderLabel}>No history data available</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tab}>
      <ProfileSection
        title="Platform Performance"
        scope={scope}
        hint="Subscribers, revenue and subscriber flow each quarter. The oldest quarter has no churn or net figure: it has no quarter before it to compare with."
        controls={
          !region && (
            <SegmentedControl
              variant="field"
              options={SPLIT_OPTS}
              value={split}
              defaultValue="total"
              onChange={(v) => setSplit(v as 'total' | 'regions')}
            />
          )
        }
      >
        <QuarterlyPerformanceTable key={split} rows={tableRows} initialRows={splitting ? 16 : 8} />
      </ProfileSection>

      <ProfileSection
        title="Performance Over Time"
        scope={scope}
        hint="Platform revenue (subscribers × ARPU) per quarter, stacked by region, with subscribers on the right axis."
      >
        <PerformanceOverTimeChart
          platform={id}
          total={history}
          byRegion={byRegion}
          regions={regions}
          scopeLabel={scopeLabel}
          revenueRank={ranks.revenue ? { rank: ranks.revenue, of: ranks.total } : undefined}
        />
      </ProfileSection>

      <ProfileSection
        title="Net, Gross, Churn Over Time"
        scope={scope}
        hint="Gross additions are new subscribers in the quarter, churns are subscribers lost, and net additions are the difference."
      >
        <NetGrossChurnChart platform={id} history={history} />
      </ProfileSection>
    </div>
  );
}
